import { useState, useEffect, useRef } from 'react';
import { 
  Video, Play, Download, RefreshCw, Sparkles, Clock, 
  CheckCircle, XCircle, AlertCircle, BookOpen, Wand2,
  Music, Upload, Image, Trash2, Volume2, Star, CreditCard,
  Eye, Bell, Film
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Tipos de Booktrailer
const BOOKTRAILER_TYPES = [
  {
    id: 'essential',
    name: 'Booktrailer Essential',
    icon: '📕',
    description: 'Portada + Audio Sample. Simple y efectivo.',
    badge: 'Recomendado'
  },
  {
    id: 'ai',
    name: 'Booktrailer IA',
    icon: '🤖',
    description: 'Video generado con IA Sora 2. Más creativo.',
    badge: 'Premium'
  }
];

// Plantillas de prompts predefinidos para IA
const PROMPT_TEMPLATES = [
  {
    id: 'dramatic',
    name: 'Dramático',
    icon: '🎭',
    description: 'Estilo cinematográfico con tensión y misterio',
    template: 'Cinematic book trailer with dramatic lighting, mysterious atmosphere, slow camera movements through dark corridors, ethereal mist, and suspenseful mood for a book titled "{title}"'
  },
  {
    id: 'romantic',
    name: 'Romántico',
    icon: '💕',
    description: 'Suave, emotivo y lleno de calidez',
    template: 'Romantic and emotional book trailer with warm golden lighting, soft focus, gentle camera movements through beautiful landscapes, floating petals, and dreamy atmosphere for a love story titled "{title}"'
  },
  {
    id: 'adventure',
    name: 'Aventura',
    icon: '🗺️',
    description: 'Épico, emocionante y lleno de acción',
    template: 'Epic adventure book trailer with sweeping aerial shots, majestic mountains, ancient ruins, dramatic sky, fast-paced transitions, and heroic atmosphere for an adventure book titled "{title}"'
  },
  {
    id: 'fantasy',
    name: 'Fantasía',
    icon: '🧙',
    description: 'Mágico con elementos sobrenaturales',
    template: 'Magical fantasy book trailer with enchanted forests, glowing magical particles, mystical creatures silhouettes, ethereal lighting, and otherworldly atmosphere for a fantasy novel titled "{title}"'
  },
  {
    id: 'thriller',
    name: 'Thriller',
    icon: '🔍',
    description: 'Tenso, oscuro y lleno de suspenso',
    template: 'Intense thriller book trailer with noir lighting, rain-soaked streets, shadowy figures, quick cuts, high contrast imagery, and tension-building atmosphere for a thriller titled "{title}"'
  },
  {
    id: 'selfhelp',
    name: 'Desarrollo Personal',
    icon: '🌱',
    description: 'Inspirador y motivacional',
    template: 'Inspirational self-help book trailer with sunrise over mountains, person silhouette achieving goals, uplifting transitions, bright and hopeful lighting, motivational atmosphere for a book titled "{title}"'
  }
];

const DURATION_OPTIONS = [
  { value: 4, label: '4 segundos', price: 'Básico' },
  { value: 8, label: '8 segundos', price: 'Estándar' },
  { value: 12, label: '12 segundos', price: 'Premium' }
];

const SIZE_OPTIONS = [
  { value: '1280x720', label: 'HD Horizontal (16:9)', icon: '📺' },
  { value: '1024x1024', label: 'Cuadrado (1:1)', icon: '⬛' },
  { value: '1024x1792', label: 'Vertical/Stories (9:16)', icon: '📱' }
];

const BooktrailerGenerator = ({ user, token }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [booktrailerType, setBooktrailerType] = useState('essential'); // 'essential' or 'ai'
  const [selectedTemplate, setSelectedTemplate] = useState('dramatic');
  const [customPrompt, setCustomPrompt] = useState('');
  const [workTitle, setWorkTitle] = useState('');
  const [duration, setDuration] = useState(4);
  const [size, setSize] = useState('1280x720');
  const [history, setHistory] = useState([]);
  const [currentGeneration, setCurrentGeneration] = useState(null);
  const [profileData, setProfileData] = useState(null);
  
  // Essential Booktrailer states
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [selectedWork, setSelectedWork] = useState(null);
  const [usePublicalaAudio, setUsePublicalaAudio] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchHistory();
    fetchProfileData();
  }, [token]);

  useEffect(() => {
    // Polling para verificar estado de generación
    let interval;
    if (currentGeneration && currentGeneration.status === 'processing') {
      interval = setInterval(() => {
        checkGenerationStatus(currentGeneration.id);
      }, 5000); // Check every 5 seconds
    }
    return () => clearInterval(interval);
  }, [currentGeneration]);

  const fetchProfileData = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data);
      // Pre-fill work title if user has works
      if (response.data.works && response.data.works.length > 0) {
        setWorkTitle(response.data.works[0].title);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchHistory = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/booktrailer/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if any video just completed (was processing, now completed)
      const oldProcessingIds = history.filter(b => b.status === 'processing' || b.status === 'pending').map(b => b.id);
      const newlyCompleted = response.data.filter(b => 
        b.status === 'completed' && oldProcessingIds.includes(b.id)
      );
      
      // Show notification for newly completed videos
      if (newlyCompleted.length > 0) {
        newlyCompleted.forEach(video => {
          toast.success(
            `🎬 ¡Tu Booktrailer "${video.work_title}" está listo!`,
            {
              description: 'Haz clic en "Ver" para reproducirlo o descargarlo.',
              duration: 10000,
              action: {
                label: 'Ver',
                onClick: () => setPreviewVideo(video)
              }
            }
          );
        });
      }
      
      setHistory(response.data);
      
      // Check if there's a processing video
      const processing = response.data.find(b => b.status === 'processing' || b.status === 'pending');
      if (processing) {
        setCurrentGeneration(processing);
      } else {
        setCurrentGeneration(null);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  // State for video preview modal
  const [previewVideo, setPreviewVideo] = useState(null);

  // Manejar selección de obra (para Essential)
  const handleWorkSelection = (work) => {
    setSelectedWork(work);
    setWorkTitle(work.title);
    if (work.cover_image) {
      setCoverPreview(work.cover_image);
      setCoverImage(null); // Clear uploaded file
    }
    // Check if work has audio sample from Publica.la
    if (work.audio_sample_url) {
      setUsePublicalaAudio(true);
    }
  };

  // Manejar upload de audio (máx 5 min = 300 segundos)
  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validar tipo de archivo
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/x-m4a'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato de audio no válido. Usa MP3, WAV o M4A.');
      return;
    }
    
    // Validar tamaño (máx 50MB para audio de 5 min)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('El archivo de audio debe ser menor a 50MB');
      return;
    }
    
    // Validar duración
    const audio = new Audio();
    audio.src = URL.createObjectURL(file);
    audio.onloadedmetadata = () => {
      if (audio.duration > 300) { // 5 minutos
        toast.error('El audio debe ser de máximo 5 minutos');
        URL.revokeObjectURL(audio.src);
        return;
      }
      
      setAudioFile(file);
      setAudioPreview(URL.createObjectURL(file));
      setUsePublicalaAudio(false);
      toast.success(`Audio cargado: ${Math.floor(audio.duration)}s`);
    };
  };

  // Manejar upload de portada
  const handleCoverUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }
    
    setCoverImage(file);
    setCoverPreview(URL.createObjectURL(file));
    toast.success('Portada cargada');
  };

  // Limpiar audio
  const clearAudio = () => {
    if (audioPreview) URL.revokeObjectURL(audioPreview);
    setAudioFile(null);
    setAudioPreview(null);
    setUsePublicalaAudio(false);
  };

  // Limpiar portada
  const clearCover = () => {
    if (coverPreview && coverImage) URL.revokeObjectURL(coverPreview);
    setCoverImage(null);
    setCoverPreview(null);
  };

  const checkGenerationStatus = async (id) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/booktrailer/status/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentGeneration(response.data);
      
      if (response.data.status === 'completed') {
        toast.success('¡Tu booktrailer está listo!');
        fetchHistory();
      } else if (response.data.status === 'failed') {
        toast.error('Error al generar el video: ' + (response.data.error || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const generateBooktrailer = async () => {
    if (!workTitle.trim()) {
      toast.error('Por favor ingresa el título de tu libro');
      return;
    }

    // Validación específica para Essential
    if (booktrailerType === 'essential') {
      if (!coverPreview) {
        toast.error('Por favor sube o selecciona una portada');
        return;
      }
      if (!audioFile && !usePublicalaAudio) {
        toast.error('Por favor sube un audio sample o selecciona usar el de Publica.la');
        return;
      }
    }

    setGenerating(true);
    try {
      if (booktrailerType === 'essential') {
        // Booktrailer Essential: Portada + Audio
        const formData = new FormData();
        formData.append('work_title', workTitle);
        formData.append('type', 'essential');
        
        // Agregar audio
        if (audioFile) {
          formData.append('audio', audioFile);
        } else if (usePublicalaAudio && selectedWork?.audio_sample_url) {
          formData.append('audio_url', selectedWork.audio_sample_url);
        }
        
        // Agregar portada
        if (coverImage) {
          formData.append('cover', coverImage);
        } else if (coverPreview) {
          formData.append('cover_url', coverPreview);
        }

        const response = await axios.post(
          `${BACKEND_URL}/api/booktrailer/generate-essential`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );

        toast.success('Generación iniciada. Tu booktrailer estará listo en unos segundos...');
        setCurrentGeneration({
          id: response.data.booktrailer_id,
          status: 'pending',
          work_title: workTitle,
          type: 'essential'
        });
        
        // Limpiar archivos locales
        clearAudio();
        
      } else {
        // Booktrailer IA: Sora 2
        let finalPrompt;
        if (customPrompt.trim()) {
          finalPrompt = customPrompt;
        } else {
          const template = PROMPT_TEMPLATES.find(t => t.id === selectedTemplate);
          finalPrompt = template.template.replace('{title}', workTitle);
        }

        const response = await axios.post(
          `${BACKEND_URL}/api/booktrailer/generate`,
          {
            prompt: finalPrompt,
            work_title: workTitle,
            duration: duration,
            size: size
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Generación IA iniciada. Esto puede tomar varios minutos...');
        setCurrentGeneration({
          id: response.data.booktrailer_id,
          status: 'pending',
          work_title: workTitle,
          type: 'ai'
        });
      }
      
    } catch (error) {
      console.error('Error generating booktrailer:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar la generación');
    } finally {
      setGenerating(false);
    }
  };

  const downloadVideo = async (id) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/booktrailer/download/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `booktrailer_${id}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Video descargado');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error al descargar el video');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1 text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full text-xs">
            <Clock className="w-3 h-3" />
            En cola
          </span>
        );
      case 'processing':
        return (
          <span className="flex items-center gap-1 text-blue-600 bg-blue-100 px-2 py-1 rounded-full text-xs">
            <RefreshCw className="w-3 h-3 animate-spin" />
            Generando...
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-green-600 bg-green-100 px-2 py-1 rounded-full text-xs">
            <CheckCircle className="w-3 h-3" />
            Completado
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1 text-red-600 bg-red-100 px-2 py-1 rounded-full text-xs">
            <XCircle className="w-3 h-3" />
            Error
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
          <Video className="w-5 h-5 text-violet-600" />
          Generador de Booktrailers
        </h3>
        <p className="text-sm text-gray-600">
          Crea videos promocionales para tus libros. Elige entre un booktrailer simple con tu portada y audio, 
          o un video generado con IA.
        </p>
      </div>

      {/* MIS BOOKTRAILERS - Sección Destacada */}
      {history.length > 0 && (
        <div className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-2xl p-1">
          <div className="bg-white rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-900 flex items-center gap-2 text-lg">
                <Film className="w-5 h-5 text-violet-600" />
                Mis Booktrailers ({history.length})
              </h4>
              <span className="text-xs text-gray-500">
                {history.filter(h => h.status === 'completed').length} completados
              </span>
            </div>
            
            <div className="grid gap-4">
              {history.slice(0, 3).map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between p-4 rounded-xl border ${
                    item.status === 'completed' 
                      ? 'bg-green-50 border-green-200' 
                      : item.status === 'processing' || item.status === 'pending'
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Thumbnail/Preview */}
                    <div className="relative w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {item.status === 'completed' && item.video_url ? (
                        <div className="w-full h-full bg-violet-600 flex items-center justify-center cursor-pointer group"
                             onClick={() => setPreviewVideo(item)}>
                          <Play className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
                        </div>
                      ) : item.status === 'processing' || item.status === 'pending' ? (
                        <div className="w-full h-full bg-blue-100 flex items-center justify-center">
                          <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <p className="font-medium text-gray-900">{item.work_title || 'Sin título'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          item.type === 'essential' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {item.type === 'essential' ? 'Essential' : 'IA'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(item.created_at).toLocaleDateString('es-MX', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {getStatusBadge(item.status)}
                    {item.status === 'completed' && (
                      <>
                        <Button
                          onClick={() => setPreviewVideo(item)}
                          size="sm"
                          variant="outline"
                          className="text-violet-600 border-violet-200"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => downloadVideo(item.id)}
                          size="sm"
                          className="bg-violet-600 hover:bg-violet-700 text-white"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {history.length > 3 && (
              <p className="text-sm text-gray-500 text-center mt-3">
                Y {history.length - 3} más en tu historial...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {previewVideo && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewVideo(null)}>
          <div className="bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">{previewVideo.work_title}</h3>
                <p className="text-sm text-gray-400">
                  {previewVideo.type === 'essential' ? 'Booktrailer Essential' : 'Booktrailer IA'}
                </p>
              </div>
              <Button
                onClick={() => setPreviewVideo(null)}
                variant="ghost"
                className="text-white hover:bg-gray-800"
              >
                ✕
              </Button>
            </div>
            
            <div className="aspect-video bg-black">
              {previewVideo.video_url ? (
                <video 
                  src={previewVideo.video_url} 
                  controls 
                  autoPlay 
                  className="w-full h-full"
                >
                  Tu navegador no soporta el elemento video.
                </video>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  Video no disponible
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-700 flex justify-between items-center">
              <p className="text-sm text-gray-400">
                Generado el {new Date(previewVideo.created_at).toLocaleDateString('es-MX', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
              <Button
                onClick={() => downloadVideo(previewVideo.id)}
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar Video
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Current Generation Status */}
      {currentGeneration && (currentGeneration.status === 'pending' || currentGeneration.status === 'processing') && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-900">Generando tu Booktrailer...</h4>
              <p className="text-sm text-blue-700">
                "{currentGeneration.work_title}" - {currentGeneration.type === 'essential' ? 'Unos segundos...' : 'Puede tomar de 2 a 10 minutos.'}
              </p>
            </div>
            {getStatusBadge(currentGeneration.status)}
          </div>
        </div>
      )}

      {/* Booktrailer Type Selection */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-medium text-gray-900 mb-4">Tipo de Booktrailer</h4>
        <div className="grid md:grid-cols-2 gap-4">
          {BOOKTRAILER_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => setBooktrailerType(type.id)}
              className={`relative p-5 rounded-xl border-2 transition-all text-left ${
                booktrailerType === type.id
                  ? 'border-violet-500 ring-2 ring-violet-200 bg-violet-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-3xl mb-2 block">{type.icon}</span>
              <span className="font-bold text-gray-900 block text-lg">{type.name}</span>
              <span className="text-sm text-gray-600">{type.description}</span>
              {type.badge && (
                <span className={`absolute top-3 right-3 text-xs px-2 py-1 rounded-full ${
                  type.id === 'essential' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {type.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Work Selection */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-violet-600" />
          {booktrailerType === 'essential' ? 'Selecciona tu Obra' : 'Título de tu Libro'}
        </h4>
        
        {/* Quick select from user's works */}
        {profileData?.works && profileData.works.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {profileData.works.map((work) => (
              <button
                key={work.isbn || work.title}
                onClick={() => handleWorkSelection(work)}
                className={`relative p-3 rounded-xl border-2 transition-all text-left ${
                  selectedWork?.title === work.title
                    ? 'border-violet-500 ring-2 ring-violet-200 bg-violet-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {work.cover_image ? (
                  <img 
                    src={work.cover_image} 
                    alt={work.title}
                    className="w-full h-24 object-cover rounded-lg mb-2"
                  />
                ) : (
                  <div className="w-full h-24 bg-gray-100 rounded-lg mb-2 flex items-center justify-center">
                    <BookOpen className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <span className="text-xs font-medium text-gray-700 line-clamp-2">{work.title}</span>
                {work.audio_sample_url && (
                  <span className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <Music className="w-3 h-3 text-white" />
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        
        <input
          type="text"
          value={workTitle}
          onChange={(e) => setWorkTitle(e.target.value)}
          placeholder="Ej: El Misterio de la Casa Abandonada"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all"
        />
      </div>

      {/* ESSENTIAL: Cover & Audio Upload */}
      {booktrailerType === 'essential' && (
        <>
          {/* Cover Image */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Image className="w-4 h-4 text-violet-600" />
              Portada del Libro
            </h4>
            
            {coverPreview ? (
              <div className="flex items-start gap-4">
                <img 
                  src={coverPreview} 
                  alt="Portada" 
                  className="w-32 h-44 object-cover rounded-xl shadow-lg"
                />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">Portada seleccionada</p>
                  <Button
                    onClick={clearCover}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Cambiar
                  </Button>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-violet-500 transition-colors">
                <Upload className="w-10 h-10 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600 font-medium">Subir portada</span>
                <span className="text-xs text-gray-400">PNG, JPG hasta 5MB</span>
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleCoverUpload} 
                />
              </label>
            )}
          </div>

          {/* Audio Sample */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Music className="w-4 h-4 text-violet-600" />
              Audio Sample (máx. 5 minutos)
            </h4>
            
            {audioPreview ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-xl">
                  <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                    <Volume2 className="w-5 h-5 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{audioFile?.name || 'Audio cargado'}</p>
                    <audio ref={audioRef} src={audioPreview} controls className="w-full h-8 mt-1" />
                  </div>
                  <Button
                    onClick={clearAudio}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : usePublicalaAudio && selectedWork?.audio_sample_url ? (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">Usando audio de Publica.la</p>
                  <p className="text-xs text-green-700">Audio sample de tu obra</p>
                </div>
                <Button
                  onClick={() => setUsePublicalaAudio(false)}
                  variant="outline"
                  size="sm"
                  className="text-gray-600 border-gray-200"
                >
                  Cambiar
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-violet-500 transition-colors">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600 font-medium">Subir audio sample</span>
                  <span className="text-xs text-gray-400">MP3, WAV, M4A - Máx. 5 min</span>
                  <input 
                    type="file" 
                    accept="audio/*" 
                    className="hidden" 
                    onChange={handleAudioUpload} 
                  />
                </label>
                
                {selectedWork?.audio_sample_url && (
                  <button
                    onClick={() => setUsePublicalaAudio(true)}
                    className="w-full p-3 rounded-xl border-2 border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-all flex items-center justify-center gap-2"
                  >
                    <Music className="w-4 h-4" />
                    Usar audio de Publica.la
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* AI: Template Selection */}
      {booktrailerType === 'ai' && (
        <>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600" />
              Estilo del Video
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {PROMPT_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => {
                    setSelectedTemplate(template.id);
                    setCustomPrompt('');
                  }}
                  className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                    selectedTemplate === template.id && !customPrompt
                      ? 'border-violet-500 ring-2 ring-violet-200 bg-violet-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl mb-2 block">{template.icon}</span>
                  <span className="font-medium text-gray-900 block">{template.name}</span>
                  <span className="text-xs text-gray-500">{template.description}</span>
                </button>
              ))}
            </div>

            {/* Custom Prompt */}
            <div className="mt-4">
              <label className="text-sm text-gray-600 mb-2 block">
                O escribe tu propio prompt (opcional):
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Describe exactamente cómo quieres que sea tu video... (en inglés para mejores resultados)"
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all resize-none"
              />
            </div>
          </div>

          {/* Duration & Size */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-violet-600" />
                Duración
              </h4>
              <div className="space-y-2">
                {DURATION_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setDuration(option.value)}
                    className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                      duration === option.value
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs text-gray-500">{option.price}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Video className="w-4 h-4 text-violet-600" />
                Formato
              </h4>
              <div className="space-y-2">
                {SIZE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSize(option.value)}
                    className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                      size === option.value
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span>{option.icon}</span>
                      <span className="font-medium">{option.label}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Generate Button */}
      <Button
        onClick={generateBooktrailer}
        disabled={generating || !workTitle.trim() || (currentGeneration?.status === 'processing')}
        className="w-full py-6 text-lg bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
      >
        {generating ? (
          <>
            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
            Iniciando generación...
          </>
        ) : (
          <>
            <Wand2 className="w-5 h-5 mr-2" />
            {booktrailerType === 'essential' ? 'Crear Booktrailer Essential' : 'Generar Booktrailer IA'}
          </>
        )}
      </Button>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="font-medium text-gray-900 mb-4">Historial de Booktrailers</h4>
          <div className="space-y-3">
            {history.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Video className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{item.work_title || 'Sin título'}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(item.created_at).toLocaleDateString('es-MX', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(item.status)}
                  {item.status === 'completed' && (
                    <Button
                      onClick={() => downloadVideo(item.id)}
                      size="sm"
                      variant="outline"
                      className="text-violet-600 border-violet-200"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upsell: Booktrailer Premium */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl p-1">
        <div className="bg-gray-900 rounded-xl p-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Star className="w-10 h-10 text-white" />
              </div>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <span className="bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded">PREMIUM</span>
                <span className="text-amber-400 text-sm">Servicio Profesional</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                ¿Te gustaría crear un Booktrailer Premium?
              </h3>
              <p className="text-white/70 text-sm mb-3">
                Nuestro equipo de producción profesional creará un booktrailer cinematográfico único para tu obra. 
                Incluye: edición profesional, música licenciada, efectos visuales de alta calidad y revisiones ilimitadas.
              </p>
              <ul className="text-white/60 text-xs space-y-1 mb-4">
                <li>✓ Producción profesional por expertos en video</li>
                <li>✓ Música y efectos de sonido licenciados</li>
                <li>✓ Entrega en múltiples formatos y resoluciones</li>
                <li>✓ Tiempo de entrega: 5-7 días hábiles</li>
              </ul>
            </div>
            
            <div className="flex flex-col items-center gap-3">
              <div className="text-center">
                <p className="text-white/50 text-xs line-through">$12,997 MXN</p>
                <p className="text-3xl font-bold text-amber-400">$7,997</p>
                <p className="text-white/70 text-xs">MXN (IVA incluido)</p>
              </div>
              <Button
                onClick={async () => {
                  try {
                    toast.loading('Creando sesión de pago...');
                    const response = await axios.post(
                      `${BACKEND_URL}/api/checkout/product`,
                      {
                        product_id: 'booktrailer_premium',
                        product_name: 'Booktrailer Premium Profesional',
                        price_mxn: 7997,
                        origin_url: window.location.origin
                      },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.dismiss();
                    if (response.data.checkout_url) {
                      window.location.href = response.data.checkout_url;
                    } else {
                      toast.error('Error al crear la sesión de pago');
                    }
                  } catch (error) {
                    toast.dismiss();
                    console.error('Error:', error);
                    toast.error(error.response?.data?.detail || 'Error al procesar el pago');
                  }
                }}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold px-6 py-3 rounded-xl shadow-lg shadow-orange-500/30 transition-all hover:scale-105"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Haz click aquí
              </Button>
              <p className="text-white/40 text-xs text-center">
                Pago seguro con Stripe
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800">
              <strong>Nota:</strong> La generación de video con IA puede tomar entre 2 y 10 minutos 
              dependiendo de la duración seleccionada. Puedes cerrar esta página y volver más tarde 
              para descargar tu video.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BooktrailerGenerator;
